import React, { useCallback, useMemo } from "react";
import PropTypes from "prop-types";
import { TableCell, makeStyles, Grid, Checkbox, Typography, Tooltip } from "@material-ui/core";
import productPageStyle from "features/products/styles";
import { get, set, sequence } from "@totalsoft/change-tracking-react";
import { useTranslation } from "react-i18next";
import { FiberManualRecord } from "@material-ui/icons";
import { emptyArray } from "constants/defaultValues";
import { cellParamTypeName, amountTypeIds } from "constants/scheduleType";
import moment from "moment";
import { onCheckBoxChange } from "utils/propertyChangeAdapters";

const useStyles = makeStyles(productPageStyle);

const getScheduleLine = (scheduleLines, cellDate) =>
  scheduleLines?.find((line) => {
    const amountType = line.values.find((column) => column.columnName === "AmountTypeId");
    if (Number(amountType.value) !== amountTypeIds.Installment) return false;
    const dueDateColumn = line.values.find((column) => column.columnName === "DueDate");
    const dueDate = new Date(dueDateColumn.value);
    return moment(dueDate).isSame(cellDate, "month");
  });

const ScheduleTableCell = (props) => {
  const { t } = useTranslation();
  const { month, productLens, productSchedule, quote, cellParamDefinitions, settingsLens } = props;
  const settings = settingsLens |> get;
  const product = productLens |> get;

  const installmentCell = useMemo(() => {
    if (settings.firstCellStartDate > month.date || month.date > settings.lastCellEndDate) {
      return null;
    }

    const scheduleLine = getScheduleLine(productSchedule.scheduleLines, month.date);

    if (scheduleLine) {
      const cellValue = scheduleLine?.values.find((x) => x.isDisplayed);
      if (cellValue) return { value: cellValue.value };
      const amountCellValue = scheduleLine?.values.find((x) => x.columnName === "Amount")?.value;
      const interestAmountCellValue = scheduleLine?.values.find((x) => x.columnName === "InterestAmount")?.value;
      return { value: Number(amountCellValue) + Number(interestAmountCellValue) };
    } else {
      return { value: 0 };
    }
  }, [productSchedule.scheduleLines, month.date, settings]);

  const downpaymentCell = useMemo(() => {
    if (product.useRentalDownPayment) return null;
    const date = moment(settings.firstCellStartDate).subtract(1, "days").startOf("month").toDate();
    if (!moment(date).isSame(month.date, "month")) return null;
    return { value: product?.showAmountsWithVAT ? product.downPaymentAmountWithVAT : product.downPaymentAmount };
  }, [
    month.date,
    product.downPaymentAmount,
    settings.firstCellStartDate,
    product?.showAmountsWithVAT,
    product.downPaymentAmountWithVAT,
    product.useRentalDownPayment,
  ]);

  const residualCell = useMemo(() => {
    if (!product?.residualValueVisible) return null;
    const date = moment(settings.lastCellEndDate).add(1, "days").startOf("month").toDate();
    if (!moment(date).isSame(month.date, "month")) return null;
    return { value: product?.showAmountsWithVAT ? product.residualValueAmountWithVAT : product.residualValueAmount };
  }, [
    month.date,
    product.residualValueAmount,
    settings.lastCellEndDate,
    product?.residualValueVisible,
    product.residualValueAmountWithVAT,
    product.showAmountsWithVAT,
  ]);

  const cellParamConfigurations = useMemo(
    () =>
      product?.cellParamsConfigurations?.find((x) => moment(x.monthDate).isSame(month.date, "month"))?.params ||
      emptyArray,
    [product?.cellParamsConfigurations, month.date]
  );

  const styleProps = useMemo(() => ({ downpaymentCell, residualCell }), [downpaymentCell, residualCell]);
  const classes = useStyles(styleProps);

  const handleCellSelect = useCallback(
    (value) => {
      const yearLens = (settingsLens.years |> sequence).find((x) => (x |> get).name === month.year);
      const monthLens = (yearLens.months |> sequence).find((x) => moment((x |> get).date).isSame(month.date, "day"));
      (monthLens.selected |> set)(value);
    },
    [month, settingsLens.years]
  );

  if (!installmentCell && !downpaymentCell && !residualCell) {
    return <TableCell className={classes.scheduleCell}></TableCell>;
  }

  return (
    <TableCell className={classes.scheduleCell}>
      <Grid
        container
        direction="column"
        alignItems="center"
        justifyContent="center"
        className={classes.scheduleTableCell}
      >
        <Grid item className={classes.scheduleTableCellGrid1}>
          {downpaymentCell && (
            <Tooltip title={t("Product.DownPayment")}>
              <Typography variant="caption" className={classes.scheduleCellLabel}>
                {quote.showAmountsWithVAT ? t("Product.DownPaymentWithVAT") : t("Product.DownPayment")}
              </Typography>
            </Tooltip>
          )}
          {residualCell && (
            <Tooltip title={t("Product.ResidualPayment")}>
              <Typography variant="caption" className={classes.scheduleCellLabel}>
                {quote.showAmountsWithVAT ? t("Product.ResidualPaymentWithVAT") : t("Product.ResidualPayment")}
              </Typography>
            </Tooltip>
          )}
          {installmentCell && (
            <Checkbox
              size="small"
              checked={month?.selected || false}
              onClick={handleCellSelect |> onCheckBoxChange}
              className={classes.padding0}
            />
          )}
        </Grid>

        <Grid item container justifyContent="center" className={classes.scheduleTableCellGrid2}>
          {installmentCell &&
            cellParamConfigurations.map((cellParamConfig, idx) => {
              const cellParamDef = cellParamDefinitions.find(
                (x) => x.cellParamName === cellParamConfig.scheduleTypeCellParamCode
              );

              if (
                !cellParamDef ||
                !cellParamConfig?.value ||
                (cellParamDef.cellParamTypeName === cellParamTypeName.Boolean && cellParamConfig.value === "false")
              )
                return;
              return (
                <FiberManualRecord key={idx} className={classes.optionDot} style={{ color: cellParamDef.color }} />
              );
            })}
        </Grid>

        <Grid item className={classes.scheduleTableCellGrid3}>
          <Tooltip
            title={`${quote.currencyCode} ${t("DECIMAL", {
              number: (installmentCell || downpaymentCell || residualCell).value,
            })}`}
          >
            <Typography variant="caption" className={classes.minFont}>
              {`${quote.currencyCode} ${t("DECIMAL", {
                number: (installmentCell || downpaymentCell || residualCell).value,
              })}`}
            </Typography>
          </Tooltip>
        </Grid>
      </Grid>
    </TableCell>
  );
};

ScheduleTableCell.propTypes = {
  month: PropTypes.object.isRequired,
  productSchedule: PropTypes.object.isRequired,
  productLens: PropTypes.object.isRequired,
  quote: PropTypes.object.isRequired,
  cellParamDefinitions: PropTypes.array.isRequired,
  settingsLens: PropTypes.object.isRequired,
};

export default ScheduleTableCell;
